import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { TrendingUp, TrendingDown, Bitcoin, LineChart, RefreshCw } from "lucide-react";

function fmtPrice(v) {
  if (v === null || v === undefined) return "—";
  return Number(v).toLocaleString("de-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function PriceRow({ item }) {
  const up = (item.change_pct || 0) >= 0;
  const Arrow = up ? TrendingUp : TrendingDown;
  return (
    <div className="grid grid-cols-[1fr_auto_auto] gap-3 items-center py-2 px-3 hover:bg-secondary/40 transition-colors" data-testid={`market-row-${item.symbol}`}>
      <div className="min-w-0">
        <div className="text-sm font-medium truncate">{item.name}</div>
        <div className="text-[10px] font-mono text-muted-foreground tracking-wider">{item.symbol}</div>
      </div>
      <div className="text-right text-sm font-mono whitespace-nowrap">{fmtPrice(item.price)}</div>
      <div className={`inline-flex items-center gap-0.5 text-xs font-bold whitespace-nowrap px-1.5 py-0.5 rounded ${up ? "text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-300" : "text-prime bg-rose-50 dark:bg-rose-900/30"}`}>
        <Arrow className="w-3 h-3" strokeWidth={2.5} />
        {up ? "+" : ""}
        {(item.change_pct || 0).toFixed(2)}%
      </div>
    </div>
  );
}

export default function MarketSnapshot() {
  const [data, setData] = useState({ indices: [], crypto: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (force = false) => {
    if (force) setRefreshing(true);
    try {
      const r = await api.get("/market/snapshot");
      setData(r.data || { indices: [], crypto: [] });
    } catch { /* */ }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(() => load(false), 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="prime-card overflow-hidden" data-testid="market-snapshot">
      <div className="flex items-center justify-between px-5 py-3 border-b border-border">
        <div>
          <div className="label-uppercase text-muted-foreground">Märkte & Crypto</div>
          <h2 className="font-display font-bold text-xl tracking-tight">Übersicht</h2>
        </div>
        <button
          type="button" onClick={() => load(true)}
          className={`text-muted-foreground hover:text-foreground p-1 rounded-full ${refreshing ? "animate-spin text-prime" : ""}`}
          title="Aktualisieren"
          data-testid="market-refresh"
        >
          <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-muted-foreground">Lädt dati di mercato…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
          {/* Indici */}
          <div>
            <div className="flex items-center gap-2 px-3 pt-3 pb-1.5 text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
              <LineChart className="w-3.5 h-3.5" strokeWidth={1.5} /> Hauptindizes
            </div>
            <div className="divide-y divide-border">
              {data.indices.length === 0 ? (
                <div className="px-3 py-6 text-center text-xs text-muted-foreground">Daten nicht verfügbar</div>
              ) : data.indices.map((i) => <PriceRow key={i.symbol} item={i} />)}
            </div>
          </div>
          {/* Crypto */}
          <div>
            <div className="flex items-center gap-2 px-3 pt-3 pb-1.5 text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
              <Bitcoin className="w-3.5 h-3.5 text-amber-500" strokeWidth={1.7} /> Top Crypto
            </div>
            <div className="divide-y divide-border">
              {data.crypto.length === 0 ? (
                <div className="px-3 py-6 text-center text-xs text-muted-foreground">Daten nicht verfügbar</div>
              ) : data.crypto.map((c) => <PriceRow key={c.symbol} item={c} />)}
            </div>
          </div>
        </div>
      )}

      <div className="px-5 py-2 bg-secondary/40 text-[10px] font-mono text-muted-foreground border-t border-border flex items-center justify-between">
        <span>Quelle: Yahoo Finance + CoinGecko</span>
        {data.as_of && <span>{new Date(data.as_of).toLocaleTimeString("de-CH")}</span>}
      </div>
    </div>
  );
}
